import { useImageSource } from "@/hooks/images/useImageSource";
import { getWhatsappClicksMetrics } from "@/infrastructure/services/metricsService";
import type {
  WhatsappClicksMetrics,
  WhatsappMetricProduct,
} from "@/types/whatsappMetrics";
import { Dialog, Icon, Loading } from "@uigovpe/components";
import { useEffect, useState } from "react";
import PerformanceHighlight from "./PerformanceHighlight";

type Props = { visible: boolean; onHide: () => void };

function ProductInterestRow({
  product,
  position,
}: {
  product: WhatsappMetricProduct;
  position: number;
}) {
  const { imageSource, isLoading } = useImageSource(product.imagemPrincipalUrl);

  return (
    <div className="grid! min-h-20! grid-cols-[2rem_3rem_minmax(0,1fr)_auto_auto]! items-center! gap-3! rounded-xl! bg-white! px-4! py-3! shadow-md! max-[479px]:relative! max-[479px]:min-h-24! max-[479px]:grid-cols-[2rem_3rem_minmax(0,1fr)]! max-[479px]:gap-2! max-[479px]:pr-12! max-[479px]:pl-3!">
      <strong className="flex! h-7! w-7! items-center! justify-center! rounded-full! bg-[#4e3873]! text-xs! text-white!">
        {position}
      </strong>
      <span className="flex! h-12! w-12! items-center! justify-center! overflow-hidden! rounded-lg! bg-[#f6f2fc]!">
        {imageSource && !isLoading ? (
          <img
            src={imageSource}
            alt=""
            className="h-full! w-full! object-cover!"
          />
        ) : (
          <Icon icon="image" outline className="text-[#4e3873]!" />
        )}
      </span>
      <span className="min-w-0!">
        <strong className="block! truncate! text-sm! text-[#494c57]! max-[479px]:line-clamp-2! max-[479px]:whitespace-normal!">
          {product.nome}
        </strong>
        <small className="mt-0.5! block! truncate! text-xs! text-[#6b7280]!">
          {[product.categoria, product.nicho].filter(Boolean).join(" / ")}
        </small>
      </span>
      <span className="flex! items-center! gap-1.5! text-xs! text-[#494c57]! max-[479px]:hidden!">
        <Icon
          icon="visibility"
          outline
          className="text-base! text-[#4e3873]!"
        />
        {product.visualizacoes.toLocaleString("pt-BR")}
      </span>
      <span className="flex! items-center! gap-1.5! text-xs! text-[#494c57]! max-[479px]:absolute! max-[479px]:top-1/2! max-[479px]:right-3! max-[479px]:-translate-y-1/2!">
        <Icon icon="call" outline className="text-base! text-[#4e3873]!" />
        {product.cliquesWhatsapp.toLocaleString("pt-BR")}
      </span>
    </div>
  );
}

export default function WhatsappClicksDialog({ visible, onHide }: Props) {
  const [data, setData] = useState<WhatsappClicksMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    let active = true;
    setIsLoading(true);
    setError(null);
    getWhatsappClicksMetrics()
      .then((response) => active && setData(response))
      .catch(() => {
        if (active)
          setError("Não foi possível carregar os cliques no WhatsApp.");
      })
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, [visible]);

  const performance = data?.melhorDesempenho;
  const trafficOrigins = performance?.origensTrafego
    .map((origin) => origin.label)
    .filter(Boolean)
    .join(" e ");

  return (
    <Dialog
      visible={visible}
      header={
        <div className="whatsapp-clicks-dialog-header flex! w-full! items-center! gap-4!">
          <span className="text-[20px]! leading-7! font-bold! text-[#494c57]! max-[479px]:text-base! max-[479px]:leading-6!">
            Produtos com mais interesse
          </span>
          <button
            type="button"
            className="whatsapp-clicks-view-all shrink-0! cursor-pointer! border-0! bg-transparent! p-0! text-xs! font-semibold! text-[#4e3873]!"
            aria-disabled="true"
          >
            Ver tudo →
          </button>
        </div>
      }
      onHide={onHide}
      draggable={false}
      closable={false}
      className="profile-views-dialog whatsapp-clicks-dialog w-[min(94vw,640px)]! bg-white!"
    >
      <section className="rounded-2xl! p-4! sm:p-6!">
        {isLoading ? (
          <div
            className="flex! h-64! items-center! justify-center!"
            role="status"
          >
            <Loading className="text-[#4e3873]!" />
            <span className="sr-only">Carregando cliques no WhatsApp...</span>
          </div>
        ) : error ? (
          <div
            className="flex! h-64! items-center! justify-center! text-sm! text-red-700!"
            role="alert"
          >
            {error}
          </div>
        ) : (
          <>
            {data?.produtos.length ? (
              <div className="flex! flex-col! gap-2!">
                {data.produtos.map((product, index) => (
                  <ProductInterestRow
                    key={product.produtoId}
                    product={product}
                    position={index + 1}
                  />
                ))}
              </div>
            ) : (
              <p className="py-8! text-center! text-sm! text-[#6b7280]!">
                Nenhum clique registrado.
              </p>
            )}

            {performance && (
              <PerformanceHighlight>
                Seus produtos em{" "}
                <strong>{performance.nicho || performance.categoria}</strong>{" "}
                tiveram um aumento médio de{" "}
                <strong>
                  {performance.variacaoMediaVisualizacoes.toLocaleString(
                    "pt-BR",
                    { maximumFractionDigits: 1 },
                  )}
                  %
                </strong>{" "}
                nas visualizações esta semana
                {trafficOrigins ? (
                  <>
                    {" "}
                    devido ao tráfego de <strong>{trafficOrigins}</strong>.
                  </>
                ) : (
                  "."
                )}
              </PerformanceHighlight>
            )}
          </>
        )}
      </section>
    </Dialog>
  );
}
